// Minimal Server-Sent-Events parsing for the extraction progress stream
// (api.ts → /extract-all-facts with Accept: text/event-stream). The server
// emits one `progress` event per record and a terminal `done` event; this
// turns the raw byte stream into discrete {event, data} objects.
//
// Pure functions, no I/O — unit-tested via node --test. The fetch reader loop
// in api.ts feeds bytes here; the buffering (events can split across network
// chunks) is the part worth testing, so it lives here, not inline.

export interface SseEvent {
  event: string;
  data: string;
}

// Parse one SSE block (the text between blank-line separators) into an event.
// `event:` defaults to "message" per the spec; multiple `data:` lines join
// with newlines. Returns null for a block with no data (e.g. a bare comment).
export function parseSseBlock(block: string): SseEvent | null {
  let event = "message";
  const dataLines: string[] = [];
  for (const rawLine of block.split("\n")) {
    const line = rawLine.replace(/\r$/, "");
    if (line.startsWith(":")) continue; // comment / heartbeat line
    const colon = line.indexOf(":");
    const field = colon === -1 ? line : line.slice(0, colon);
    // A value has its single leading space stripped, per the SSE spec.
    let value = colon === -1 ? "" : line.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    if (field === "event") event = value;
    else if (field === "data") dataLines.push(value);
  }
  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join("\n") };
}

// Pull every COMPLETE event out of an accumulating buffer, returning the parsed
// events and the unconsumed tail (a partial event still arriving). The caller
// keeps appending bytes to `rest` and draining again. Events are separated by a
// blank line — "\n\n" (also tolerated: "\r\n\r\n").
export function drainSseEvents(buffer: string): {
  events: SseEvent[];
  rest: string;
} {
  const normalized = buffer.replace(/\r\n/g, "\n");
  const events: SseEvent[] = [];
  let rest = normalized;
  let idx: number;
  while ((idx = rest.indexOf("\n\n")) !== -1) {
    const block = rest.slice(0, idx);
    rest = rest.slice(idx + 2);
    const evt = parseSseBlock(block);
    if (evt) events.push(evt);
  }
  return { events, rest };
}
